import { BufferGeometry, CircleGeometry, Color, Float32BufferAttribute, Group, Mesh, MeshBasicMaterial, Vector3 } from 'three';
import type { HillLayer, Look } from './look';

/**
 * Two or three flat layers of hills, like a paper theatre set: unlit rings of
 * silhouette, each paler (hazier) than the one in front of it. The day's
 * setting (look.ts) says what they are: rolling hills, jagged peaks with snow
 * on top, land only where there's no sea, a volcano.
 */
function layer(l: HillLayer, seed: number, cx: number, cz: number, extra: { pos: number[]; col: number[] } | null = null): Mesh {
  const segs = l.segments ?? 360;
  const pos: number[] = [];
  const col: number[] = [];
  const round = (a: number): number => {
    let h = 0;
    h += Math.sin(a * 2 + seed) * 0.45;
    h += Math.sin(a * 5 + seed * 1.7) * 0.28;
    h += Math.sin(a * 11 + seed * 2.3) * 0.14;
    h += Math.sin(a * 23 + seed * 0.7) * 0.06;
    return 0.55 + 0.45 * h;
  };
  // Peaks: sharp cusps at several sizes (1 - |sin| points up), a few big ones standing over the rest.
  const jagged = (a: number): number => {
    const spike = (x: number): number => 1 - Math.abs(Math.sin(x));
    const h = 0.5 * spike(a * 4 + seed) ** 2 + 0.32 * spike(a * 11 + seed * 1.7) ** 1.5 + 0.18 * spike(a * 27 + seed * 2.3);
    return 0.12 + 0.88 * h;
  };
  const top = (a: number): number => {
    let h = l.base + l.amp * (l.shape === 'jagged' ? jagged(a) : round(a)) + (l.bump?.(a) ?? 0);
    // Where there's no land (a sea), the ring sinks out of sight under it.
    if (l.arc) h = -6 + (h + 6) * l.arc(a);
    return h;
  };
  const snowAt = l.snow ? l.base + l.amp * l.snow.above : Infinity;
  // One colour for the whole ring (Tuscany's), or a colour per vertex (snow on the tops, smoke over it).
  const coloured = !!l.snow || !!extra;
  const quad = (x0: number, z0: number, x1: number, z1: number, a0: number, a1: number, b0: number, b1: number, c: Color | null): void => {
    pos.push(x0, a0, z0, x1, a1, z1, x1, b1, z1, x0, a0, z0, x1, b1, z1, x0, b0, z0);
    if (coloured) for (let k = 0; k < 6; k++) col.push((c ?? l.color).r, (c ?? l.color).g, (c ?? l.color).b);
  };
  for (let i = 0; i < segs; i++) {
    const a0 = (i / segs) * Math.PI * 2;
    const a1 = ((i + 1) / segs) * Math.PI * 2;
    const x0 = cx + Math.cos(a0) * l.radius;
    const z0 = cz + Math.sin(a0) * l.radius;
    const x1 = cx + Math.cos(a1) * l.radius;
    const z1 = cz + Math.sin(a1) * l.radius;
    const h0 = top(a0);
    const h1 = top(a1);
    const y0 = -30;
    if (!l.snow) {
      quad(x0, z0, x1, z1, y0, y0, h0, h1, null);
      continue;
    }
    // Rock up to the snow line, snow above it (a crisp edge, poster-style).
    const s0 = Math.min(h0, snowAt);
    const s1 = Math.min(h1, snowAt);
    quad(x0, z0, x1, z1, y0, y0, s0, s1, l.color);
    if (h0 > snowAt || h1 > snowAt) quad(x0, z0, x1, z1, s0, s1, h0, h1, l.snow.color);
  }
  if (extra) {
    pos.push(...extra.pos);
    col.push(...extra.col);
  }
  const g = new BufferGeometry();
  g.setAttribute('position', new Float32BufferAttribute(pos, 3));
  if (coloured) g.setAttribute('color', new Float32BufferAttribute(col, 3));
  const mat = coloured ? new MeshBasicMaterial({ vertexColors: true, fog: false, side: 2 }) : new MeshBasicMaterial({ color: l.color, fog: false, side: 2 });
  const m = new Mesh(g, mat);
  m.frustumCulled = false;
  return m;
}

/** The sky's colour at an elevation (degrees), as sky.ts paints it. */
function skyAt(sky: Look['sky'], e: number): Color {
  const band = (at: number, soft: number): number => {
    const t = Math.max(0, Math.min(1, (e - (at - soft)) / (2 * soft)));
    return t * t * (3 - 2 * t);
  };
  const c = sky.horizon.clone();
  c.lerp(sky.low, band(2, 0.15));
  c.lerp(sky.mid, band(6, 0.2));
  c.lerp(sky.high, band(13.5, 0.6));
  c.lerp(sky.top, band(24, 2.5));
  return c;
}

/**
 * A volcano's smoke (Etna), in the far ring's own mesh (no extra draw): flat puffs rising off the summit and
 * lying over on the wind, each mixed into the sky behind it as it thins (so they need no transparency).
 */
function smokeOf(
  l: HillLayer,
  summitAt: number,
  summitH: number,
  cx: number,
  cz: number,
  v: { smoke: Color },
  sky: Look['sky'],
): { pos: number[]; col: number[] } {
  const pos: number[] = [];
  const col: number[] = [];
  const puffs = 16;
  // Faintest first, so the thicker ones (nearer the camera by a hair) come out on top.
  for (let k = puffs - 1; k >= 0; k--) {
    const u = k / (puffs - 1);
    const rise = 420 * Math.sqrt(u);
    const drift = 0.28 * u ** 1.4;
    const wob = Math.sin(k * 2.3) * 0.012 * (1 + u);
    const a = summitAt + drift + wob;
    const r = l.radius - 60 - (puffs - k) * 2;
    const x = cx + Math.cos(a) * r;
    const z = cz + Math.sin(a) * r;
    const y = summitH + 40 + rise + Math.sin(k * 1.7) * 25;
    const rad = 55 + u * 210 + Math.sin(k * 3.1) * 18;
    const alpha = 0.55 * (1 - u) ** 0.8 + 0.06;
    const c = skyAt(sky, (Math.atan2(y, r) * 180) / Math.PI).lerp(v.smoke, alpha);
    const disc = new CircleGeometry(rad, 12);
    disc.lookAt(new Vector3(cx - x, 0, cz - z));
    disc.translate(x, y, z);
    const p = disc.toNonIndexed().getAttribute('position');
    for (let n = 0; n < p.count; n++) {
      pos.push(p.getX(n), p.getY(n), p.getZ(n));
      col.push(c.r, c.g, c.b);
    }
  }
  return { pos, col };
}

/** The backdrop's rings, far to near (each ring's shape seeded as Tuscany's always were: +3.1, +1.3, +0). */
export function makeHills(
  cx: number,
  cz: number,
  seed: number,
  layers: HillLayer[],
  volcano: { sunYaw: number; yawFromSun: number; smoke: Color } | null = null,
  sky: Look['sky'] | null = null,
): Group {
  const g = new Group();
  const offsets = [3.1, 1.3, 0];
  layers.forEach((l, i) => {
    const s = seed + (offsets[i] ?? i * 0.7);
    let extra = null;
    if (i === 0 && volcano && sky) {
      // The summit: the far ring's height where the volcano stands (its bump, flattened at the top).
      const at = -(volcano.sunYaw + volcano.yawFromSun);
      let h = 0;
      h += Math.sin(at * 2 + s) * 0.45;
      h += Math.sin(at * 5 + s * 1.7) * 0.28;
      h += Math.sin(at * 11 + s * 2.3) * 0.14;
      h += Math.sin(at * 23 + s * 0.7) * 0.06;
      const summit = l.base + l.amp * (0.55 + 0.45 * h) + (l.bump?.(at) ?? 0);
      extra = smokeOf(l, at, summit, cx, cz, volcano, sky);
    }
    g.add(layer(l, s, cx, cz, extra));
  });
  g.children.forEach((c, i) => (c.renderOrder = -9 + i));
  return g;
}
